import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Loading } from '../components/ui/loading.js';
import { Toaster } from '../components/ui/sonner.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { OrganizationProvider } from './OrganizationContext.js';
import { AppSidebarProvider } from './SidebarContext.js';
import { ThemeProvider } from './ThemeContext.js';

const AppContext = createContext(undefined);
/**
 * Fetches application configuration from the backend
 * @returns Promise resolving to AppConfig object
 * @throws Error if config fetch fails
 */
async function fetchAppConfig() {
    const response = await fetch('/api/v1/public/config');
    if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status}`);
    }
    return await response.json();
}
/**
 * Internal config provider component that manages config fetching and state
 * @param props - Component props including children and optional auth/config overrides
 * @returns JSX element providing config context
 */
function AppConfigProvider({ children, auth, configOverride, }) {
    const [config, setConfig] = useState(configOverride ?? null);
    const [loading, setLoading] = useState(!configOverride);
    useEffect(() => {
        if (configOverride)
            return;
        fetchAppConfig()
            .then(fetchedConfig => {
            setConfig(fetchedConfig);
            setLoading(false);
        })
            .catch(error => {
            console.error('Failed to fetch app config:', error);
            setLoading(false);
        });
    }, [configOverride]);
    const getApiUrl = useCallback(() => config?.apiUrl, [config]);
    const contextValue = {
        config,
        loading,
        apiUrl: config?.apiUrl,
        getApiUrl,
        auth: auth ?? null,
    };
    return jsx(AppContext.Provider, { value: contextValue, children: children });
}
/**
 * Main application context provider that wraps all other providers.
 * QueryClientProvider is outside AppConfigConsumer so it's always available.
 * @param props - Component props including children, optional defaultTheme, auth adapter, and config
 * @returns JSX element with full application context
 */
function AppContextProvider({ defaultTheme, children, auth, config }) {
    // Use useState to keep QueryClient stable across renders
    const [queryClient] = useState(() => new QueryClient({
        defaultOptions: {
            queries: {
                retry: 3,
                refetchOnWindowFocus: false,
            },
        },
    }));
    return (jsx(QueryClientProvider, { client: queryClient, children: jsx(AppConfigProvider, { auth: auth, configOverride: config, children: jsx(ThemeProvider, { defaultTheme: defaultTheme, children: jsx(AppConfigConsumer, { children: jsx(OrganizationProvider, { children: jsxs(AppSidebarProvider, { children: [children, jsx(Toaster, {})] }) }) }) }) }) }));
}
/**
 * Consumer component that handles loading state and config validation
 * @param props - Component props including children
 * @returns JSX element or loading screen
 */
function AppConfigConsumer({ children }) {
    const { loading } = useApp();
    if (loading) {
        return jsx(Loading, { fullScreen: true, size: "lg", text: "Initializing application..." });
    }
    return jsx(Fragment, { children: children });
}
/**
 * Hook to access app configuration context
 * @returns AppContextType containing config, loading state, and utility functions
 * @throws Error if used outside of AppContextProvider
 */
function useApp() {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error('useApp must be used within an AppContextProvider');
    }
    return context;
}

export { AppContextProvider, useApp };
//# sourceMappingURL=AppContext.js.map
